import {groupBy} from 'remeda'

import Checkbox from '@/components/Checkbox/Checkbox'
import ProductPrice from '@/components/ProductCard/ProductPrice'
import QuantitySelector from '@/components/QuantitySelector/QuantitySelector'
import {useCurrencySymbol} from '@/lib/locale/country'
import type {CartItem} from '@/models/cartModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './BuyAgainItemGroups.module.scss'

interface ItemProps {
  item: CartItem
  onSelect?: MemoizedCallback<(item: CartItem, state: boolean) => void>
  onQuantityChange?: MemoizedCallback<(item: CartItem, quantity: number) => void>
  quantities: Map<string, number>
  selection: Map<string, boolean>
  isAvailable?: boolean
}

const BuyAgainItem = memo(function BuyAgainItem({
  item,
  onSelect,
  onQuantityChange,
  quantities,
  selection,
  isAvailable,
}: ItemProps) {
  const currencySymbol = useCurrencySymbol()

  const onChange = useCallback(
    (value: boolean) => {
      onSelect?.(item, value)
    },
    [item, onSelect],
  )

  const onQtyChange = useCallback(
    (quantity: number) => {
      onQuantityChange?.(item, quantity)
    },
    [item, onQuantityChange],
  )

  const quantity = quantities.get(`${item.retailer}-${item.id}`) ?? 1
  const totalPrice = item.price * quantity
  const isSelected = selection.get(`${item.retailer}-${item.id}`) ?? false

  return (
    <div
      className={clsx(style.Item, !isAvailable && style.IsDisabled)}
      key={`${item.retailer}-${item.id}`}
    >
      <div className={style.Col1}>
        <div className={style.Selection}>
          {isAvailable && (
            <Checkbox
              label={`Buy "${item.title}" again`}
              onChange={onChange}
              isSelected={isSelected}
            />
          )}
          {!isAvailable && (
            <div
              role='checkbox'
              tabIndex={0}
              aria-checked={false}
              style={{width: '1.125rem', height: '1.125rem', border: 'none', background: 'none'}}
              aria-label={`Item "${item.title}" is unavailable`}
              aria-disabled
            />
          )}
        </div>
        <div className={style.ItemImage}>
          <img src={item.image} alt='' aria-hidden='true' loading='lazy' />
        </div>
        <div className={style.ItemInfo}>
          <VisuallyHidden>Product name: </VisuallyHidden>
          <Link
            to='/product/$productId'
            params={{
              productId: {
                retailer: item.retailer,
                id: item.id,
              },
            }}
            className={style.ItemTitle}
          >
            {item.title}
          </Link>
          {item.variation && (
            <div className={style.ItemOptions}>
              {Object.entries(item.variation)
                .map(([key, value]) => `${key.slice(0, 1).toUpperCase() + key.slice(1)}: ${value}`)
                .join(' | ')}
            </div>
          )}
          <div className={style.ItemPrice}>
            <VisuallyHidden>Price per item: </VisuallyHidden>
            {currencySymbol}
            {item.price}
          </div>
        </div>
      </div>
      <div className={style.QuantityAndPrice}>
        <div className={style.Quantity}>
          <VisuallyHidden>Quantity: </VisuallyHidden>
          {isAvailable && (
            <QuantitySelector
              quantity={quantity}
              setQuantity={onQtyChange}
              maxQuantity={10}
              allowZero={false}
            />
          )}
        </div>
        <div className={style.Price}>
          <ProductPrice price={totalPrice} />
        </div>
      </div>
    </div>
  )
})

interface Props {
  items: (CartItem & {isAvailable?: boolean})[]
  onSelect?: MemoizedCallback<(item: CartItem, state: boolean) => void>
  onQuantityChange?: MemoizedCallback<(item: CartItem, quantity: number) => void>
  quantities: Map<string, number>
  selection: Map<string, boolean>
}

export default memo(function BuyAgainItemGroups({
  items,
  onSelect,
  onQuantityChange,
  quantities,
  selection,
}: Props) {
  const groupedItems = groupBy(items, item => item.retailer)

  return (
    <div className={style.ItemGroups}>
      {Object.entries(groupedItems).map(([, items]) =>
        items.map(item => (
          <BuyAgainItem
            key={`${item.retailer}-${item.id}`}
            isAvailable={item.isAvailable}
            item={item}
            onSelect={onSelect}
            onQuantityChange={onQuantityChange}
            quantities={quantities}
            selection={selection}
          />
        )),
      )}
    </div>
  )
})
